<template>
  <div class="model-manager">
    <div class="flex justify-between items-center mb-6">
      <div>
        <h2 class="text-2xl font-semibold text-gray-900 dark:text-gray-200">
          Models
        </h2>
        <p class="text-gray-600 dark:text-gray-400 mt-1">Manage your models</p>
      </div>
      <div class="flex gap-2">
        <button
          class="text-gray-700 dark:text-gray-200 px-3 py-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors flex items-center gap-2 border-2 border-primary"
          @click="fetchModels"
        >
          <Icon :path="mdiRefresh" class="w-5 h-5" />
        </button>
        <button
          class="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors flex items-center gap-2"
          @click="showCreateModal = true"
        >
          <Icon :path="mdiPlus" class="w-5 h-5" />
          Add
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex justify-center items-center py-12">
      <div
        class="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"
      ></div>
    </div>

    <!-- Error State -->
    <div
      v-else-if="error"
      class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4 text-red-600 dark:text-red-400"
    >
      {{ error }}
    </div>

    <!-- Data Table -->
    <div
      v-else
      class="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden"
    >
      <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead class="bg-gray-50 dark:bg-gray-800">
          <tr>
            <th
              class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
            >
              Name
            </th>
            <th
              class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
            >
              Type
            </th>
            <th
              class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
            >
              Status
            </th>
            <th
              class="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
            >
              Actions
            </th>
          </tr>
        </thead>
        <tbody
          class="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700"
        >
          <tr v-if="!models.length" class="text-center">
            <td colspan="4" class="px-6 py-8 text-gray-500 dark:text-gray-400">
              No models found. Create your first model to get started.
            </td>
          </tr>
          <tr
            v-for="model in models"
            :key="model.id"
            class="hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
            @click="onModelClick(model)"
          >
            <td class="px-6 py-4 whitespace-nowrap">
              <div class="font-medium text-gray-900 dark:text-gray-200">
                {{ model.name }}
              </div>
            </td>
            <td class="px-6 py-4 whitespace-nowrap">
              <span
                class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                :class="getTypeClass(model.ml_type)"
              >
                {{ model.ml_type }}
              </span>
            </td>
            <td class="px-6 py-4 whitespace-nowrap">
              <span
                class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                :class="getStatusClass(model.status)"
              >
                {{ getStatusDisplay(model.status) }}
              </span>
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-right text-sm">
              <button
                class="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 font-medium flex items-center gap-1 ml-auto"
                @click.stop="confirmDelete(model)"
              >
                <Icon :path="mdiDelete" class="w-4 h-4" />
                Delete
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Trial Notice -->
    <div
      class="mt-4 p-4 bg-primary/10 dark:bg-primary/5 rounded-lg border border-primary/20 dark:border-primary/20"
    >
      <p class="text-sm text-primary-dark dark:text-primary">
        This is a technical preview trial limited to 2 models per account and
        300 requests per minute.
        <a
          href="https://www.kappaml.com/contact"
          target="_blank"
          rel="noopener noreferrer"
          class="hover:text-primary-dark/80 dark:hover:text-primary/80 underline"
          >Contact us</a
        >
        for a full account with unlimited models, higher throughput, and
        advanced features.
      </p>
    </div>

    <!-- Add Model Modal -->
    <Modal v-model="showCreateModal" title="Create New Model">
      <div
        v-if="models.length >= 2"
        class="mb-4 text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-4 rounded-lg border border-red-200 dark:border-red-700"
      >
        You have reached the maximum limit of 2 models. Please delete an
        existing model to create a new one. If you need more models, please
        <a
          href="https://www.kappaml.com/contact"
          target="_blank"
          rel="noopener noreferrer"
          class="text-primary hover:text-primary-dark dark:hover:text-primary/80 underline"
          >contact us</a
        >.
      </div>
      <form @submit.prevent="createNewModel">
        <div class="mb-4">
          <label
            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
          >
            Model name
          </label>
          <input
            v-model="newModelName"
            type="text"
            required
            class="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
            placeholder="Enter a name for your model"
          />
          <span v-if="modelNameError" class="text-red-600 dark:text-red-400">
            {{ modelNameError }}
          </span>
        </div>
        <div class="mb-4">
          <label
            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
          >
            Model type
          </label>
          <select
            v-model="newModelType"
            required
            class="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
          >
            <option value="regression">Regression</option>
            <option value="classification">Classification</option>
            <option value="forecasting">Forecasting</option>
            <option value="clustering" disabled>Clustering</option>
            <option value="anomaly" disabled>Anomaly detection</option>
          </select>
        </div>
        <div class="flex justify-end gap-3">
          <button
            type="button"
            class="px-4 py-2 text-gray-700 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
            @click="closeCreateModal"
          >
            Cancel
          </button>
          <button
            type="submit"
            class="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
            :disabled="isCreating"
          >
            {{ isCreating ? "Adding..." : "Add model" }}
          </button>
        </div>
      </form>
    </Modal>

    <!-- Delete Confirmation Modal -->
    <Modal v-model="showDeleteModal" title="Delete Model">
      <p class="text-gray-600 dark:text-gray-400 mb-4">
        Are you sure you want to delete the model "{{ modelToDelete?.name }}"?
        This action cannot be undone.
      </p>
      <template #footer>
        <button
          class="px-4 py-2 text-gray-700 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
          @click="closeDeleteModal"
        >
          Cancel
        </button>
        <button
          class="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
          :disabled="isDeleting"
          @click="deleteModel"
        >
          <Icon v-if="!isDeleting" :path="mdiDelete" class="w-4 h-4" />
          {{ isDeleting ? "Deleting..." : "Delete" }}
        </button>
      </template>
    </Modal>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted } from "vue";
import { useRouter } from "vue-router";
import Icon from "@/components/Icon.vue";
import Modal from "@/components/shared/Modal.vue";
import { mdiPlus, mdiDelete, mdiRefresh } from "@mdi/js";
import kappamlAPI from "@/services/kappaml-api";

export default {
  name: "ModelManager",
  components: {
    Icon,
    Modal,
  },
  emits: ["model-created", "model-deleted", "tenant-not-found"],
  setup(props, { emit }) {
    const router = useRouter();
    const models = ref([]);
    const loading = ref(true);
    const error = ref(null);
    const showCreateModal = ref(false);
    const showDeleteModal = ref(false);
    const newModelName = ref("");
    const newModelType = ref("regression");
    const isCreating = ref(false);
    const isDeleting = ref(false);
    const modelToDelete = ref(null);
    const modelNameError = ref("");
    const pollingInterval = ref(null);

    const fetchModels = async (showLoading = true) => {
      try {
        if (showLoading) {
          loading.value = true;
        }
        error.value = null;
        models.value = await kappamlAPI.listModels();
      } catch (err) {
        if (err.response.data.detail === "Tenant not found") {
          emit("tenant-not-found");
        } else {
          error.value = err.message;
        }
      } finally {
        if (showLoading) {
          loading.value = false;
        }
      }
    };

    const validateModelName = (name) => {
      if (!name) {
        modelNameError.value = "Model name is required";
        return false;
      }
      if (!/^[a-zA-Z0-9-]+$/.test(name)) {
        modelNameError.value =
          "Model name can only contain letters, numbers, and dashes";
        return false;
      }
      modelNameError.value = "";
      return true;
    };

    const closeCreateModal = async () => {
      showCreateModal.value = false;
      newModelName.value = "";
      newModelType.value = "regression";
      modelNameError.value = "";
      await fetchModels();
    };

    const closeDeleteModal = async () => {
      showDeleteModal.value = false;
      modelToDelete.value = null;
      await fetchModels();
    };

    const createNewModel = async () => {
      if (!validateModelName(newModelName.value)) {
        return;
      }

      try {
        isCreating.value = true;
        const result = await kappamlAPI.createModel({
          name: newModelName.value,
          ml_type: newModelType.value,
        });
        await closeCreateModal();
        emit("model-created", result);
      } catch (err) {
        error.value = err.message;
      } finally {
        isCreating.value = false;
      }
    };

    const confirmDelete = (model) => {
      modelToDelete.value = model;
      showDeleteModal.value = true;
    };

    const deleteModel = async () => {
      if (!modelToDelete.value) return;

      try {
        isDeleting.value = true;
        await kappamlAPI.deleteModel(modelToDelete.value.id);
        await closeDeleteModal();
        emit("model-deleted", modelToDelete.value);
      } catch (err) {
        error.value = err.message;
      } finally {
        isDeleting.value = false;
      }
    };

    const formatDate = (date) => {
      if (!date) return "-";
      return new Date(date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    };

    const getTypeClass = (type) => {
      const classes = {
        classification: "bg-blue-100 text-blue-800",
        regression: "bg-green-100 text-green-800",
        clustering: "bg-purple-100 text-purple-800",
      };
      return classes[type] || "bg-gray-100 text-gray-800";
    };

    const getStatusClass = (status) => {
      const states = {
        PENDING: "Pending",
        DEPLOYING: "Deploying",
        DEPLOYED: "Deployed",
        FAILED: "Failed",
        DELETING: "Deleting",
      };

      const classes = {
        [states.PENDING]: "bg-yellow-100 text-yellow-800",
        [states.DEPLOYING]: "bg-blue-100 text-blue-800",
        [states.DEPLOYED]: "bg-green-100 text-green-800",
        [states.FAILED]: "bg-red-100 text-red-800",
        [states.DELETING]: "bg-gray-100 text-gray-800",
      };

      return classes[status] || classes[states.PENDING];
    };

    const getStatusDisplay = (status) => {
      return status || "Pending";
    };

    const onModelClick = (model) => {
      router.push({ name: "model", params: { id: model.id } });
    };

    onMounted(() => {
      fetchModels(true); // Show loading on initial fetch
      // Set up polling every 30 seconds without showing loading state
      pollingInterval.value = setInterval(() => fetchModels(false), 30000);
    });

    onUnmounted(() => {
      // Clean up the interval when component is unmounted
      if (pollingInterval.value) {
        clearInterval(pollingInterval.value);
      }
    });

    return {
      models,
      loading,
      error,
      showCreateModal,
      showDeleteModal,
      newModelName,
      newModelType,
      isCreating,
      isDeleting,
      modelToDelete,
      modelNameError,
      createNewModel,
      confirmDelete,
      deleteModel,
      formatDate,
      getTypeClass,
      getStatusClass,
      getStatusDisplay,
      onModelClick,
      mdiPlus,
      mdiDelete,
      mdiRefresh,
      closeCreateModal,
      closeDeleteModal,
      fetchModels,
    };
  },
};
</script>

<style scoped>
.model-manager {
  @apply max-w-6xl mx-auto;
}
</style>
